<template>
  <div class="d-flex" style="height: 100vh;">
    <!-- Panel de herramientas -->
    <v-navigation-drawer permanent width="400" class="overflow-y-auto">
      <v-container>
        <v-card class="mb-6" outlined>
          <v-card-title class="text-subtitle-1 py-2">Generar Certificado</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="6">
                <v-btn color="primary" block @click="previewPDF" :loading="isGenerating" class="mb-2">
                  <v-icon left>mdi-eye</v-icon>
                  Previsualizar
                </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn color="success" block @click="generatePDF" :loading="isGenerating" class="mb-2">
                  <v-icon left>mdi-download</v-icon>
                  Descargar PDF
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-row>
          <v-col cols="12">
            <h2 class="text-h5 font-weight-bold mb-4">Configurador de Certificados</h2>

            <!-- Configuración de papel -->
            <v-card class="mb-6" outlined>
              <v-card-title class="text-subtitle-1 py-2">Tamaño de papel</v-card-title>
              <v-card-text>
                <v-select v-model="paperSize" :items="paperSizeOptions" item-text="label" item-value="key" outlined
                  dense></v-select>
              </v-card-text>
            </v-card>

            <!-- Imagen de fondo -->
            <v-card class="mb-6" outlined>
              <v-card-title class="text-subtitle-1 py-2">Imagen de fondo</v-card-title>
              <v-card-text>
                <v-file-input accept="image/*" label="Subir fondo" prepend-icon="mdi-upload"
                  @change="handleBackgroundUpload" outlined dense></v-file-input>
              </v-card-text>
            </v-card>

            <!-- Lista de elementos -->
            <v-card class="mb-6" outlined>
              <v-card-title class="text-subtitle-1 py-2">
                Elementos ({{ elements.length }})
              </v-card-title>
              <v-card-text class="pa-0">
                <div style="max-height: 200px; overflow-y: auto;">
                  <v-list v-if="elements.length > 0" dense>
                    <v-list-item v-for="element in elements" :key="element.id" @click="selectedElement = element.id"
                      :class="{ 'v-list-item--active': selectedElement === element.id }">
                      <v-list-item-icon class="mr-2">
                        <v-icon :color="getElementIconColor(element.type)" small>
                          {{ getElementIcon(element.type) }}
                        </v-icon>
                      </v-list-item-icon>

                      <v-list-item-content>
                        <v-list-item-title class="text-body-2">
                          {{ element.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                          {{ pxToCm(element.x) }}cm, {{ pxToCm(element.y) }}cm
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action class="flex-row">
                        <v-btn icon x-small @click.stop="toggleElementVisibility(element.id)"
                          :color="element.visible ? 'default' : 'error'">
                          <v-icon small>
                            {{ element.visible ? 'mdi-eye' : 'mdi-eye-off' }}
                          </v-icon>
                        </v-btn>
                        <v-btn icon x-small color="error" @click.stop="deleteElement(element.id)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                  <div v-else class="pa-4 text-center text--secondary">
                    <span class="text-body-2">No hay elementos agregados</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Herramientas -->
            <v-card class="mb-6" outlined>
              <v-card-title class="text-subtitle-1 py-2">Agregar elementos</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="4">
                    <v-btn color="success" block @click="addElement('text')" class="flex-column" style="height: 60px;">
                      <v-icon>mdi-format-text</v-icon>
                      <span class="text-caption">Texto</span>
                    </v-btn>
                  </v-col>
                  <v-col cols="4">
                    <v-btn color="purple" block @click="addElement('image')" class="flex-column" style="height: 60px;">
                      <v-icon>mdi-image</v-icon>
                      <span class="text-caption">Imagen</span>
                    </v-btn>
                  </v-col>
                  <v-col cols="4">
                    <v-btn color="orange" block @click="addElement('qr')" class="flex-column" style="height: 60px;">
                      <v-icon>mdi-qrcode</v-icon>
                      <span class="text-caption">QR</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Propiedades del elemento seleccionado -->
            <v-card v-if="selectedEl" outlined>
              <v-card-title class="text-subtitle-1 py-2 d-flex justify-space-between align-center">
                <span>Propiedades</span>
                <v-btn icon small color="error" @click="deleteElement(selectedEl.id)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text>
                <!-- Nombre del elemento -->
                <v-text-field v-model="selectedEl.name" label="Nombre del elemento" outlined dense class="mb-3"
                  @input="updateSelectedElement({ name: $event })"></v-text-field>

                <!-- Propiedades específicas por tipo -->
                <template v-if="selectedEl.type === 'text'">
                  <v-textarea v-model="selectedEl.content" label="Texto" outlined dense rows="3" class="mb-3"
                    @input="updateSelectedElement({ content: $event })"></v-textarea>

                  <v-text-field v-model.number="selectedEl.fontSize" label="Tamaño de fuente (pt)" type="number"
                    outlined dense min="8" max="72" class="mb-3"
                    @input="updateSelectedElement({ fontSize: parseInt($event) || 16 })"></v-text-field>

                  <v-select v-model="selectedEl.fontFamily" :items="fontOptions" label="Fuente" outlined dense
                    class="mb-3" @change="updateSelectedElement({ fontFamily: $event })"></v-select>

                  <div class="mb-3">
                    <v-subheader class="pl-0">Alineación</v-subheader>
                    <v-btn-toggle v-model="selectedEl.textAlign" mandatory dense
                      @change="updateSelectedElement({ textAlign: $event })">
                      <v-btn small value="left">
                        <v-icon small>mdi-format-align-left</v-icon>
                      </v-btn>
                      <v-btn small value="center">
                        <v-icon small>mdi-format-align-center</v-icon>
                      </v-btn>
                      <v-btn small value="right">
                        <v-icon small>mdi-format-align-right</v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </div>

                  <div class="mb-3">
                    <v-subheader class="pl-0">Color</v-subheader>
                    <input type="color" v-model="selectedEl.color"
                      @input="updateSelectedElement({ color: $event.target.value })" class="v-input--dense"
                      style="width: 100%; height: 40px; border: 1px solid #ccc; border-radius: 4px;" />
                  </div>
                </template>

                <template v-if="selectedEl.type === 'image'">
                  <v-file-input accept="image/*" label="Subir imagen" prepend-icon="mdi-upload"
                    @change="handleImageUpload" outlined dense class="mb-3"></v-file-input>
                </template>

                <template v-if="selectedEl.type === 'qr'">
                  <v-text-field v-model="selectedEl.content" label="Contenido QR" placeholder="URL o texto" outlined
                    dense class="mb-3" @input="updateSelectedElement({ content: $event })"></v-text-field>
                </template>

                <!-- Posición y tamaño con precisión decimal -->
                <v-row class="mb-3">
                  <v-col cols="6">
                    <v-text-field :value="pxToCm(selectedEl.x)" @input="updateElementPosition('x', $event)"
                      label="X (cm)" type="number" step="0.01" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field :value="pxToCm(selectedEl.y)" @input="updateElementPosition('y', $event)"
                      label="Y (cm)" type="number" step="0.01" outlined dense></v-text-field>
                  </v-col>
                </v-row>

                <v-row class="mb-3">
                  <v-col cols="6">
                    <v-text-field :value="pxToCm(selectedEl.width)" @input="updateElementSize('width', $event)"
                      label="Ancho (cm)" type="number" step="0.01" min="0.10" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field :value="pxToCm(selectedEl.height)" @input="updateElementSize('height', $event)"
                      label="Alto (cm)" type="number" step="0.01" min="0.10" outlined dense></v-text-field>
                  </v-col>
                </v-row>

                <v-text-field v-model.number="selectedEl.rotation" label="Rotación (grados)" type="number" step="0.1"
                  min="-180" max="180" outlined dense
                  @input="updateSelectedElement({ rotation: parseFloat($event) || 0 })"></v-text-field>

                <!-- Información de coordenadas precisas -->
                <v-card outlined class="mt-3" color="grey lighten-5">
                  <v-card-text class="pa-2">
                    <div class="text-caption">
                      <strong>Coordenadas exactas:</strong><br>
                      X: {{ pxToCm(selectedEl.x) }}cm | Y: {{ pxToCm(selectedEl.y) }}cm<br>
                      W: {{ pxToCm(selectedEl.width) }}cm | H: {{ pxToCm(selectedEl.height) }}cm
                    </div>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-navigation-drawer>

    <!-- Canvas -->
    <div class="flex-grow-1" style="overflow: auto; background-color: #f5f5f5;">
      <div class="pa-4">
        <div class="canvas-container elevation-4" :style="canvasContainerStyle">
          <!-- Regla horizontal -->
          <div class="ruler-horizontal">
            <div v-for="i in horizontalMarks" :key="`h-${i}`" class="ruler-mark-h" :style="{ left: cmToPx(i) + 'px' }">
              <div class="ruler-text">{{ i }}</div>
            </div>
          </div>

          <!-- Regla vertical -->
          <div class="ruler-vertical">
            <div v-for="i in verticalMarks" :key="`v-${i}`" class="ruler-mark-v" :style="{ top: cmToPx(i) + 'px' }">
              <div class="ruler-text">{{ i }}</div>
            </div>
          </div>

          <!-- Canvas principal -->
          <div id="certificate-canvas" class="canvas-main" :style="canvasStyle" @mousemove="handleMouseMove"
            @mouseup="handleMouseUp" @mouseleave="handleMouseUp">
            <div v-for="element in elements" :key="element.id" class="element-container" :class="{
              'element-selected': selectedElement === element.id,
              'element-hidden': !element.visible
            }" :style="getElementStyle(element)" @mousedown="handleMouseDown($event, element.id)">
              <!-- Elemento de texto -->
              <div v-if="element.type === 'text'" class="element-content" :style="getTextStyle(element)">
                {{ element.content }}
              </div>

              <!-- Elemento de imagen -->
              <div v-else-if="element.type === 'image'" class="element-content image-container">
                <img v-if="element.imageUrl" :src="element.imageUrl" alt="Elemento" class="element-image" />
                <span v-else class="image-placeholder">Sin imagen</span>
              </div>

              <!-- Elemento QR -->
              <div v-else-if="element.type === 'qr'" class="element-content qr-container">
                <div class="qr-content">
                  <div class="qr-title">QR</div>
                  <div class="qr-text">
                    {{ element.content.length > 10 ? element.content.substring(0, 10) + '...' : element.content }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CertificateConfigurator',
  data() {
    return {
      isGenerating: false,
      elements: [],
      selectedElement: null,
      backgroundImage: null,
      paperSize: 'A4-landscape',
      dragOffset: { x: 0, y: 0 },
      isDragging: false,

      // DPI más preciso para conversiones exactas
      dpi: 96, // Pixels por pulgada estándar para pantalla
      cmPerInch: 2.54, // Centímetros por pulgada

      paperSizes: {
        'A4-landscape': { width: 1123, height: 795, label: 'A4 Horizontal (29.7x21 cm)' },
        'A4-portrait': { width: 795, height: 1123, label: 'A4 Vertical (21x29.7 cm)' },
        'letter-landscape': { width: 1056, height: 817, label: 'Carta Horizontal (27.9x21.6 cm)' },
        'letter-portrait': { width: 817, height: 1056, label: 'Carta Vertical (21.6x27.9 cm)' }
      },

      fontOptions: [
        'Arial',
        'Times New Roman',
        'Helvetica',
        'Georgia',
        'Courier New',
        'Verdana',
        'Amoresa'
      ]
    }
  },

  computed: {
    currentPaperSize() {
      return this.paperSizes[this.paperSize]
    },

    paperSizeOptions() {
      return Object.entries(this.paperSizes).map(([key, size]) => ({
        key,
        label: size.label
      }))
    },

    selectedEl() {
      return this.elements.find(el => el.id === this.selectedElement)
    },

    // Factor de conversión más preciso
    pixelsPerCm() {
      return this.dpi / this.cmPerInch
    },

    canvasContainerStyle() {
      return {
        position: 'relative',
        backgroundColor: 'white',
        width: (this.currentPaperSize.width + 40) + 'px',
        height: (this.currentPaperSize.height + 40) + 'px'
      }
    },

    canvasStyle() {
      return {
        width: this.currentPaperSize.width + 'px',
        height: this.currentPaperSize.height + 'px',
        backgroundImage: this.backgroundImage ? `url(${this.backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    },

    horizontalMarks() {
      return Array.from({ length: Math.ceil(this.pxToCm(this.currentPaperSize.width)) + 1 }, (_, i) => i)
    },

    verticalMarks() {
      return Array.from({ length: Math.ceil(this.pxToCm(this.currentPaperSize.height)) + 1 }, (_, i) => i)
    }
  },

  methods: {
    // Conversión precisa de píxeles a centímetros con 2 decimales
    pxToCm(px) {
      return parseFloat((px / this.pixelsPerCm).toFixed(2))
    },

    // Conversión precisa de centímetros a píxeles
    cmToPx(cm) {
      return parseFloat((cm * this.pixelsPerCm).toFixed(2))
    },

    // Función para redondear a 2 decimales
    roundToTwo(num) {
      return Math.round((num + Number.EPSILON) * 100) / 100
    },

    getElementIcon(type) {
      const icons = {
        text: 'mdi-format-text',
        image: 'mdi-image',
        qr: 'mdi-qrcode'
      }
      return icons[type] || 'mdi-help'
    },

    getElementIconColor(type) {
      const colors = {
        text: 'success',
        image: 'purple',
        qr: 'orange'
      }
      return colors[type] || 'default'
    },

    getElementStyle(element) {
      return {
        left: this.roundToTwo(element.x) + 'px',
        top: this.roundToTwo(element.y) + 'px',
        width: this.roundToTwo(element.width) + 'px',
        height: this.roundToTwo(element.height) + 'px',
        transform: `rotate(${this.roundToTwo(element.rotation)}deg)`,
        opacity: element.visible ? 1 : 0.3
      }
    },

    getTextStyle(element) {
      return {
        fontSize: element.fontSize + 'pt',
        fontFamily: element.fontFamily,
        color: element.color,
        backgroundColor: element.backgroundColor,
        textAlign: element.textAlign,
        display: 'flex',
        alignItems: 'center',
        justifyContent: this.getTextAlign(element.textAlign),
        width: '100%',
        height: '100%',
        padding: '4px',
        wordWrap: 'break-word',
        overflow: 'hidden',
        whiteSpace: 'pre-wrap'
      }
    },

    getTextAlign(align) {
      const alignments = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end'
      }
      return alignments[align] || 'center'
    },

    addElement(type) {
      const newElement = {
        id: Date.now(),
        type,
        x: this.roundToTwo(this.cmToPx(2.5)), // Posición inicial precisa
        y: this.roundToTwo(this.cmToPx(2.5)),
        width: this.roundToTwo(this.cmToPx(type === 'qr' ? 2.5 : 5.0)),
        height: this.roundToTwo(this.cmToPx(type === 'qr' ? 2.5 : type === 'image' ? 4.0 : 1.5)),
        content: type === 'text' ? 'Nuevo texto' : type === 'qr' ? 'https://ejemplo.com' : '',
        fontSize: 16,
        fontFamily: 'Arial',
        color: '#000000',
        backgroundColor: 'transparent',
        rotation: 0,
        imageUrl: null,
        textAlign: 'center',
        name: `${type === 'text' ? 'Texto' : type === 'image' ? 'Imagen' : 'QR'} ${this.elements.length + 1}`,
        visible: true
      }

      this.elements.push(newElement)
      this.selectedElement = newElement.id
    },

    updateElement(id, updates) {
      const elementIndex = this.elements.findIndex(el => el.id === id)
      if (elementIndex !== -1) {
        // Aplicar redondeo a valores numéricos de posición y tamaño
        if (updates.x !== undefined) updates.x = this.roundToTwo(updates.x)
        if (updates.y !== undefined) updates.y = this.roundToTwo(updates.y)
        if (updates.width !== undefined) updates.width = this.roundToTwo(updates.width)
        if (updates.height !== undefined) updates.height = this.roundToTwo(updates.height)
        if (updates.rotation !== undefined) updates.rotation = this.roundToTwo(updates.rotation)

        Object.assign(this.elements[elementIndex], updates)
        this.$forceUpdate()
      }
    },

    updateSelectedElement(updates) {
      if (this.selectedEl) {
        this.updateElement(this.selectedEl.id, updates)
      }
    },

    deleteElement(id) {
      this.elements = this.elements.filter(el => el.id !== id)
      if (this.selectedElement === id) {
        this.selectedElement = null
      }
    },

    toggleElementVisibility(id) {
      const element = this.elements.find(el => el.id === id)
      if (element) {
        this.updateElement(id, { visible: !element.visible })
      }
    },

    updateElementPosition(axis, value) {
      if (this.selectedEl) {
        const numValue = parseFloat(value) || 0
        const pxValue = this.cmToPx(numValue)
        const maxValue = axis === 'x' ?
          this.currentPaperSize.width - this.selectedEl.width :
          this.currentPaperSize.height - this.selectedEl.height

        const updates = {}
        updates[axis] = Math.max(0, Math.min(pxValue, maxValue))
        this.updateElement(this.selectedEl.id, updates)
      }
    },

    updateElementSize(dimension, value) {
      if (this.selectedEl) {
        const numValue = parseFloat(value) || 0.1
        const pxValue = this.cmToPx(Math.max(0.1, numValue)) // Mínimo 0.1cm

        const updates = {}
        updates[dimension] = pxValue
        this.updateElement(this.selectedEl.id, updates)
      }
    },

    handleMouseDown(e, elementId) {
      e.preventDefault()
      e.stopPropagation()

      this.selectedElement = elementId
      this.isDragging = true

      const rect = e.currentTarget.getBoundingClientRect()
      this.dragOffset = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }
    },

    handleMouseMove(e) {
      if (!this.isDragging || !this.selectedElement) return

      const container = document.getElementById('certificate-canvas')
      if (!container) return

      const containerRect = container.getBoundingClientRect()
      const element = this.elements.find(el => el.id === this.selectedElement)
      if (!element) return

      const newX = e.clientX - containerRect.left - this.dragOffset.x
      const newY = e.clientY - containerRect.top - this.dragOffset.y

      this.updateElement(this.selectedElement, {
        x: Math.max(0, Math.min(newX, this.currentPaperSize.width - element.width)),
        y: Math.max(0, Math.min(newY, this.currentPaperSize.height - element.height))
      })
    },

    handleMouseUp() {
      this.isDragging = false
    },

    handleBackgroundUpload(file) {
      if (file) {
        const reader = new FileReader()
        reader.onload = (event) => {
          this.backgroundImage = event.target.result
        }
        reader.readAsDataURL(file)
      }
    },

    handleImageUpload(file) {
      if (file && this.selectedEl) {
        const reader = new FileReader()
        reader.onload = (event) => {
          this.updateElement(this.selectedEl.id, { imageUrl: event.target.result })
        }
        reader.readAsDataURL(file)
      }
    },
    async generatePDF() {
      try {
        // Preparar la configuración para enviar al backend
        const config = {
          elements: this.elements,
          backgroundImage: this.backgroundImage,
          paperSize: this.paperSize,
          canvasWidth:this.currentPaperSize.width,
          canvasHeight:this.currentPaperSize.height          
        }

        // Mostrar loading
        this.isGenerating = true

        // Hacer la petición al backend
        const response = await fetch('http://localhost:5000/generate-certificate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(config)
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        // Obtener el blob del PDF
        const blob = await response.blob()

        // Crear URL para descarga
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        a.download = 'certificado.pdf'
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)

        // Mostrar mensaje de éxito
        this.$toast.success('Certificado generado exitosamente')

      } catch (error) {
        window.console.error('Error generating PDF:', error)
        this.$toast.error('Error al generar el certificado')
      } finally {
        this.isGenerating = false
      }
    },

    // Método para previsualizar antes de generar
    async previewPDF() {
      try {
        const config = {
          elements: this.elements,
          backgroundImage: this.backgroundImage,
          paperSize: this.paperSize,
          canvasWidth:this.currentPaperSize.width,
          canvasHeight:this.currentPaperSize.height  
        }

        const response = await fetch('http://localhost:5000/generate-certificate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(config)
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)

        // Abrir en nueva ventana para previsualización
        window.open(url, '_blank')

        // Limpiar URL después de un tiempo
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 5000)

      } catch (error) {
        window.console.error('Error previewing PDF:', error)
        this.$toast.error('Error al previsualizar el certificado')
      }
    }

  }
}
</script>

<style scoped>
/* Contenedor del canvas */
.canvas-container {
  position: relative;
  background-color: white;
  margin: 0 auto;
}

/* Reglas */
.ruler-horizontal {
  position: absolute;
  top: 0;
  left: 20px;
  height: 20px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
  z-index: 10;
}

.ruler-vertical {
  position: absolute;
  left: 0;
  top: 20px;
  width: 20px;
  background-color: #f5f5f5;
  border-right: 1px solid #ddd;
  z-index: 10;
}

.ruler-mark-h {
  position: absolute;
  top: 0;
  height: 100%;
  border-left: 1px solid #ccc;
}

.ruler-mark-v {
  position: absolute;
  left: 0;
  width: 100%;
  border-top: 1px solid #ccc;
}

.ruler-text {
  font-size: 10px;
  color: #666;
  padding: 2px;
  background-color: #f5f5f5;
}

/* Canvas principal */
.canvas-main {
  position: absolute;
  top: 20px;
  left: 20px;
  border: 2px solid #ddd;
  overflow: hidden;
  background-color: white;
}

/* Elementos */
.element-container {
  position: absolute;
  cursor: move;
  border: 2px solid transparent;
  user-select: none;
  transform-origin: center;
  transition: border-color 0.2s ease;
}

.element-container:hover {
  border-color: #2196F3 !important;
}

.element-selected {
  border-color: #2196F3 !important;
  z-index: 100;
}

.element-hidden {
  opacity: 0.3;
}

.element-content {
  width: 100%;
  height: 100%;
  pointer-events: none;
}

/* Contenedor de imagen */
.image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
}

.element-image {
  width: 100%;
  height: 100%;
  /* object-fit: contain; */
}

.image-placeholder {
  color: #999;
  font-size: 12px;
}

/* Contenedor QR */
.qr-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  color: white;
  border: 1px solid #ccc;
}

.qr-content {
  text-align: center;
  padding: 4px;
}

.qr-title {
  font-size: 12px;
  font-weight: bold;
  font-family: monospace;
}

.qr-text {
  font-size: 8px;
  font-family: monospace;
  margin-top: 2px;
}
</style>